import React, { memo, useCallback, useMemo, useRef, useState } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import { CircularProgress } from '@mui/material'
import { replacePlaceholders } from '../utils/placeholderUtil.js'
import { useProcessContext } from '../../../store.js'
import { containsKeys } from '../utils/filterUtils.js'
import FieldHistory from '../../../components/DataEntry/FieldHistory.js'

const AutoCompleteField = ({
  field,
  fieldId,
  value,
  allValues,
  listDataMetadata,
  options = [],
  onChange,
  className = '',
  disabled = false,
  label = null,
  placeholder = null,
  style = {},
  helperText = null,
  autoFocus = false,
  isPivotDataLoading = false,
  getOptionLabel = (option) => option.value,
  inputSx = {},
}) => {
  const processContext = useProcessContext((state) => state.processContext)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const inputRef = useRef(null)

  const handleInputChange = (event, value, reason) => {
    onChange(event, value, reason)
  }

  const handleDropdownOpen = () => {
    setIsDropdownOpen(true)
    setTimeout(() => {
      inputRef.current?.focus()
    }, 0)
  }
  const handleDropdownClose = () => {
    setIsDropdownOpen(false)
  }

  const fieldTemplateNamesForPlaceHolders = useMemo(() => {
    return processContext.allFields?.reduce((acc, cur) => {
      return { ...acc, [cur.id]: `[[${cur.field_template_name}]]` }
    }, {})
  }, [processContext?.allFields])

  const getActualValue = useCallback(
    (value) => {
      const finalText = replacePlaceholders(value?.value, fieldTemplateNamesForPlaceHolders)
      return { ...value, value: finalText }
    },
    [fieldTemplateNamesForPlaceHolders]
  )
  const zigzagFilterOptions = useCallback((options, { inputValue }) => {
    return options.filter((option) => containsKeys(option?.value, inputValue))
  }, [])

  const autocompleteComponent = (
    <Autocomplete
      open={isDropdownOpen}
      isOptionEqualToValue={(option, value) =>
        value?.id ? option?.id === value?.id : option?.value === value?.value
      }
      options={options.map((it) => getActualValue(it))}
      getOptionLabel={getOptionLabel}
      fullWidth
      style={style}
      className={className}
      disabled={disabled || isPivotDataLoading}
      sx={{
        '& .MuiInputBase-root': {
          height: '34.25px',
        },
        '& .MuiFormControl-root': {
          height: '34.25px',
        },
        ...inputSx,
      }}
      value={getActualValue(value)}
      onChange={handleInputChange}
      filterOptions={zigzagFilterOptions}
      onOpen={handleDropdownOpen}
      onClose={handleDropdownClose}
      openOnFocus
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          variant="outlined"
          color="primary"
          size="small"
          label={label}
          autoFocus={autoFocus}
          placeholder={placeholder}
          error={helperText != null}
          // helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isPivotDataLoading ? <CircularProgress color="primary" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  )

  if (!field || field.is_subfield) {
    return autocompleteComponent
  } else {
    return !isDropdownOpen ? (
      <FieldHistory
        fieldId={fieldId}
        field={field}
        listDataMetadata={listDataMetadata}
        dynamicListData={processContext.dynamicListData}
        processId={processContext?.processData?.id}
        allValues={allValues}
        helperText={helperText}
      >
        {autocompleteComponent}
      </FieldHistory>
    ) : (
      autocompleteComponent
    )
  }
}

export default memo(AutoCompleteField, (prevProps, nextProps) => {
  return (
    prevProps.value === nextProps.value &&
    prevProps.options === nextProps.options &&
    prevProps.isPivotDataLoading === nextProps.isPivotDataLoading &&
    prevProps.helperText === nextProps.helperText
  )
})
